import React, { useEffect, useState } from "react"

import { LinkIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { Form, useActionData, useLoaderData, useNavigate, useRevalidator } from "react-router-dom"
import { twMerge } from "tailwind-merge"

import { Bee } from "~/src/components/BeeKit"
import { basename } from "~/src/components/Controller/DashboardLookbooksRoutes"
import { Modal } from "~/src/components/Modal"
import { appClient } from "~/src/lib/appClients"

export type ShareLookbookModalProps = {
  lookbook: Lookbook
  sharedUserContacts: SharedUserContact[]
  colleagues: Colleague[]
}

export type Lookbook = {
  id: number
  isPublic: boolean
  shareLink: string
  name: string
  publicUrl: string
}

export type Colleague = {
  id: number
  email: string
  name: string
}

export type SharedWithUsersProps = {
  sharedUserContacts: SharedUserContact[]
  lookbookId: number
}

export type SharedUserContact = {
  id: number
  email: string
  name: string
  initials: string
}

type ActionData = {
  success: boolean
}

export function ShareLookbookModal() {
  const data = useLoaderData() as ShareLookbookModalProps
  const actionData = useActionData() as ActionData

  const { lookbook, sharedUserContacts, colleagues } = data
  const navigate = useNavigate()

  const [colleagueEmail, setColleagueEmail] = useState<string | undefined>(undefined)
  const [colleagueId, setColleagueId] = useState<number | undefined>(undefined)
  const [message, setMessage] = useState<string>("")
  const [error, setError] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (actionData?.success) {
      setMessage("")
      setColleagueEmail(undefined)
      setColleagueId(undefined)
    }
  }, [actionData])

  const handleInputChange = (value: object) => {
    if (value == null) return

    const [id, email] = value as [string | number, string]

    // Skipping setting ID if the user entered a custom value for email
    if (typeof id === "number") {
      setColleagueId(id)
    } else {
      setColleagueId(undefined)
    }

    if (isValidEmail(email)) {
      setColleagueEmail(email)
      setError(undefined)
    } else {
      setError("Please enter a valid email address")
      setColleagueEmail(undefined)
    }
  }

  const isValidEmail = (email: string) => {
    const regex = /\S+@\S+\.\S+/
    return regex.test(email)
  }

  const copyShareLink = (shareLink: string) => {
    navigator.clipboard?.writeText(shareLink)
    Bee.toast.success({ message: "Copied share link to your clipboard." })
  }

  return (
    <Modal
      open={true}
      className="flex w-[662px] flex-col items-center justify-start gap-4 rounded-lg bg-white p-6"
      backgroundClassName="bg-black bg-opacity-50 z-50"
      usePortal={false}
      showCloseButton={true}
      onClose={() => navigate(`..`)}
    >
      <Form
        method="post"
        action={`/${basename}/${lookbook.id}/share`}
        onSubmit={(e) => {
          if (!colleagueEmail) {
            e.preventDefault()
            setError("Email address is required")
          }
        }}
        className="contents"
      >
        <section className="flex flex-col self-stretch">
          <h1 className="pb-6 text-center text-3xl font-medium">Share {lookbook.name}</h1>
          <div className="flex flex-col">
            <div className="flex flex-col gap-1.5">
              <h2 className="font-medium">Invite with email</h2>
              <Bee.Field errorMessages={error}>
                <Bee.ComboboxInput
                  placeholder="Email address"
                  emptyText=""
                  value={colleagueId || colleagueEmail || null}
                  onChange={handleInputChange}
                  options={colleagues.map((colleague) => [colleague.id, colleague.email])}
                  allowCustomValue
                />
              </Bee.Field>
            </div>
            <Bee.HiddenInput name="user_email" value={colleagueEmail ?? ""} />
            <Bee.HiddenInput name="user_id" value={colleagueId ?? ""} />
            <Bee.HiddenInput name="ideabook_id" value={lookbook.id} />
            <Bee.TextArea
              name="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="h-[98px]"
              placeholder="Add a message (optional)"
            />
          </div>
        </section>
        {!lookbook.isPublic && sharedUserContacts.length > 0 && (
          <SharedWithUsers sharedUserContacts={sharedUserContacts} lookbookId={lookbook.id} />
        )}
        <hr className="w-full border-t border-gray-200" />
        <div className="flex w-full flex-col items-center sm:flex-row">
          <button
            type="button"
            onClick={() => copyShareLink(lookbook.publicUrl)}
            className="hidden w-full cursor-pointer items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline sm:flex"
          >
            <LinkIcon className="h-4 w-4" />
            Copy share link
          </button>

          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Bee.Button
              onClick={() => copyShareLink(lookbook.publicUrl)}
              kind="secondary"
              className="flex w-full items-center justify-center gap-1.5 border-gray-900 sm:hidden"
            >
              <LinkIcon className="h-4 w-4" />
              Copy share link
            </Bee.Button>

            <Bee.Button
              onClick={() => navigate(`..`)}
              kind="secondary"
              className="order-2 flex-grow border-0 sm:order-1 sm:flex-none"
            >
              Cancel
            </Bee.Button>

            <Bee.Button
              type="submit"
              kind="primary"
              className={twMerge("order-1 flex-grow sm:order-2 sm:flex-none", error && "cursor-not-allowed opacity-80")}
              disabled={!!error}
            >
              Share Lookbook
            </Bee.Button>
          </div>
        </div>
      </Form>
    </Modal>
  )
}

function SharedWithUsers(props: SharedWithUsersProps) {
  const { sharedUserContacts, lookbookId } = props
  const revalidator = useRevalidator()

  const removeSharedUser = async (userId: number) => {
    try {
      await appClient.delete(`${basename}/${lookbookId}/share/${userId}?class=CustomRequestUser`, {
        headers: { Accept: "application/json" },
      })
      Bee.toast.success({ message: "Successfully removed shared user." })
      revalidator.revalidate()
    } catch (error) {
      const errors = error.response?.data?.errors || ["There was an error. Please try again."]
      errors.forEach((err: string) => {
        Bee.toast.error({ message: err })
      })
    }
  }

  return (
    <div className="flex flex-grow flex-col gap-2 self-stretch overflow-y-auto">
      <h2 className="text-base font-medium">Shared with</h2>

      <div className="flex flex-col gap-3">
        {sharedUserContacts.map((user) => (
          <div key={user.id} className="flex w-full items-center gap-3">
            <Bee.Avatar initials={user.initials} />

            <div className="flex flex-grow flex-col justify-end">
              <span className="text-sm font-normal">{user.name}</span>
              <span className="text-xs font-normal text-gray-500">{user.email}</span>
            </div>

            <button type="button" onClick={() => removeSharedUser(user.id)} className="ml-auto pr-4">
              <XMarkIcon className="h-6 w-6 cursor-pointer text-gray-600" />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}
